import { useEffect, useMemo, useState } from 'react';

const loadColumnState = (storageKey) => {
  if (typeof window === 'undefined') return { visibility: {}, ordering: [], pinning: { left: [], right: [] } };
  try {
    const stored = window.localStorage.getItem(storageKey);
    if (!stored) return { visibility: {}, ordering: [], pinning: { left: [], right: [] } };
    const parsed = JSON.parse(stored);
    return {
      visibility: parsed.visibility || {},
      ordering: Array.isArray(parsed.ordering) ? parsed.ordering : [],
      pinning: parsed.pinning && typeof parsed.pinning === 'object'
        ? { left: Array.isArray(parsed.pinning.left) ? parsed.pinning.left : [], right: Array.isArray(parsed.pinning.right) ? parsed.pinning.right : [] }
        : { left: [], right: [] },
    };
  } catch (_err) {
    return { visibility: {}, ordering: [], pinning: { left: [], right: [] } };
  }
};

const loadTableState = (storageKey) => {
  if (typeof window === 'undefined') return { globalFilter: '', columnFilters: [], sorting: [] };
  try {
    const stored = window.localStorage.getItem(storageKey);
    if (!stored) return { globalFilter: '', columnFilters: [], sorting: [] };
    const parsed = JSON.parse(stored);
    return {
      globalFilter: typeof parsed?.globalFilter === 'string' ? parsed.globalFilter : '',
      columnFilters: Array.isArray(parsed?.columnFilters)
        ? parsed.columnFilters.filter((item) => item && item.id && item.value != null)
        : [],
      sorting: Array.isArray(parsed?.sorting)
        ? parsed.sorting.filter((item) => item && item.id).map((item) => ({ id: item.id, desc: Boolean(item.desc) }))
        : [],
    };
  } catch (_err) {
    return { globalFilter: '', columnFilters: [], sorting: [] };
  }
};

export const useAppsheetOrderItemsTableState = (userEmail, defaultPinning) => {
  const emailKey = userEmail ? String(userEmail).toLowerCase() : 'guest';
  const columnStorageKey = `appsheetOrderItemsTableColumns:${emailKey}`;
  const tableStateStorageKey = `appsheetOrderItemsTableState:${emailKey}`;

  const savedColumnState = useMemo(() => loadColumnState(columnStorageKey), [columnStorageKey]);
  const savedTableState = useMemo(() => loadTableState(tableStateStorageKey), [tableStateStorageKey]);

  const [columnVisibility, setColumnVisibility] = useState(savedColumnState.visibility);
  const [columnOrder, setColumnOrder] = useState(savedColumnState.ordering);
  const [columnPinning, setColumnPinning] = useState(savedColumnState.pinning);
  const [globalFilter, setGlobalFilter] = useState(savedTableState.globalFilter);
  const [columnFilters, setColumnFilters] = useState(savedTableState.columnFilters);
  const [sorting, setSorting] = useState(savedTableState.sorting);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        columnStorageKey,
        JSON.stringify({ visibility: columnVisibility, ordering: columnOrder, pinning: columnPinning })
      );
    } catch (_err) {
      // noop
    }
  }, [columnStorageKey, columnVisibility, columnOrder, columnPinning]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        tableStateStorageKey,
        JSON.stringify({ globalFilter, columnFilters, sorting })
      );
    } catch (_err) {
      // noop
    }
  }, [tableStateStorageKey, globalFilter, columnFilters, sorting]);

  const initialState = {
    density: 'compact',
    showGlobalFilter: false,
    showColumnFilters: false,
    columnPinning: savedColumnState.pinning.left.length || savedColumnState.pinning.right.length
      ? savedColumnState.pinning
      : defaultPinning,
    columnOrder: savedColumnState.ordering.length ? savedColumnState.ordering : undefined,
  };

  return {
    state: { columnVisibility, columnOrder, columnPinning, globalFilter, columnFilters, sorting },
    handlers: { setColumnVisibility, setColumnOrder, setColumnPinning, setGlobalFilter, setColumnFilters, setSorting },
    initialState,
  };
};
